// ─────────────────────────────────────────────────────────────────────────────
// vault_handle_store.ts — persistent storage for the user's chosen vault
// folder handle (FileSystemDirectoryHandle).
//
// Browsers serialize FileSystemDirectoryHandle via structuredClone, so we
// can stash the handle directly in IndexedDB and retrieve it on later boots.
// Permission still has to be re-queried each session (per spec), but the
// handle itself survives.
//
// We use a dedicated DB to keep this orthogonal to the notebook DB.
// ─────────────────────────────────────────────────────────────────────────────

const DB_NAME = 'grimoire-vault-handle';
const DB_VERSION = 1;
const STORE = 'handles';
const KEY = 'active';

let _db: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

/** Save the active vault directory handle. */
export async function saveHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(handle, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Load the saved vault directory handle, or null if none is set. */
export async function loadHandle(): Promise<FileSystemDirectoryHandle | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** Forget the active vault handle. Used when the user detaches the folder. */
export async function clearHandle(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Whether the runtime supports the File System Access API needed for
 * folder vaults. Chromium-based browsers (desktop + Android) yes;
 * Firefox / Safari / iOS no (as of April 2026).
 */
export function isFsaSupported(): boolean {
  return typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
}

/**
 * Re-check or request `readwrite` permission on a stored handle.
 * Per spec, `requestPermission` must be called from a user gesture.
 */
export async function ensurePermission(
  handle: FileSystemDirectoryHandle,
  mode: 'read' | 'readwrite' = 'readwrite',
  prompt = false,
): Promise<PermissionState> {
  // queryPermission is the silent check (works at any time)
  const opts = { mode } as FileSystemHandlePermissionDescriptor;
  const current = await handle.queryPermission(opts);
  if (current === 'granted') return 'granted';
  if (!prompt) return current;
  // requestPermission requires user gesture
  return await handle.requestPermission(opts);
}
